import { useEffect, useRef } from "react";
interface Props {
  getFrequencyData: () => Float32Array;
  sampleRate: number; fftSize: number; minFreq: number; maxFreq: number;
  threshold?: number;
  intensityRef: React.RefObject<{ intensity: number; triggered: boolean }>;
  label: string;
}
/** Selected FFT band; detector readings come directly from the stage. */
export function SpectrumMeter({ getFrequencyData, sampleRate, fftSize, minFreq, maxFreq,
  threshold, intensityRef, label }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const readingRef = useRef<HTMLOutputElement>(null);
  const meterRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current, ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    let frame = 0, lastDraw = -Infinity, triggeredUntil = 0, width = 300, height = 54;
    const resize = () => {
      width = canvas.clientWidth; height = canvas.clientHeight;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(width * ratio); canvas.height = Math.round(height * ratio);
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    };
    const observer = new ResizeObserver(resize);
    observer.observe(canvas); resize();
    const draw = (now: number) => {
      frame = requestAnimationFrame(draw);
      if (document.hidden) return;
      const intensity = intensityRef.current?.intensity ?? 0;
      if (intensityRef.current?.triggered) triggeredUntil = now + 90;
      // Keep UI repaint independent of the stage's refresh rate.
      if (now - lastDraw < 1000 / 30) return;
      lastDraw = now;
      if (readingRef.current) readingRef.current.textContent = intensity.toFixed(3);
      if (meterRef.current) meterRef.current.dataset.triggered = String(now < triggeredUntil);
      ctx.clearRect(0, 0, width, height);
      const spectrum = getFrequencyData();
      if (!spectrum.length || sampleRate <= 0) return;
      const start = Math.max(0, Math.floor(minFreq * fftSize / sampleRate));
      const end = Math.min(spectrum.length, Math.ceil(maxFreq * fftSize / sampleRate));
      const bars = Math.min(64, end - start);
      if (bars <= 0) return;
      const barWidth = width / bars;
      ctx.fillStyle = "#8196a4";
      for (let bar = 0; bar < bars; bar++) {
        const from = start + Math.floor(bar * (end - start) / bars);
        const to = start + Math.floor((bar + 1) * (end - start) / bars);
        let peak = -100;
        for (let bin = from; bin < to; bin++) peak = Math.max(peak, spectrum[bin] ?? -100);
        const level = Math.max(0, Math.min(1, (peak + 100) / 100));
        ctx.fillRect(bar * barWidth, height * (1 - level), Math.max(1, barWidth - 2), height * level);
      }
      if (threshold !== undefined) {
        ctx.strokeStyle = "#c0dce8"; ctx.lineWidth = 1; ctx.setLineDash([3, 3]);
        ctx.beginPath(); ctx.moveTo(0, height * (1 - threshold));
        ctx.lineTo(width, height * (1 - threshold)); ctx.stroke(); ctx.setLineDash([]);
      }
    };
    frame = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(frame); observer.disconnect(); };
  }, [getFrequencyData, sampleRate, fftSize, minFreq, maxFreq, threshold, intensityRef]);
  return <div className="spectrum-meter" ref={meterRef}>
    <canvas ref={canvasRef} className="spectrum-plot" aria-label={label + "频谱"} />
    <div className="meter-reading"><span>{threshold === undefined ? "Intensity" : "Current"}</span>
      <output ref={readingRef} aria-label={label + "实时强度"}>0.000</output></div>
  </div>;
}
